/**
 * Contact — replaces the /contact ComingSoonPage stub (contest brief,
 * 2026-07-11).
 *
 * Email-first and honest: the addresses below are the real, live inboxes
 * (hello@ / support@ / privacy@ / legal@), all read by the founder. No
 * contact form — no forms that pretend a support desk exists. Expectations
 * are stated plainly.
 */
import { Mail, Clock, MessageSquareText } from "lucide-react";
import { useSEO } from "@/lib/seo";

const INBOXES = [
  {
    email: "hello@sonspeak.com",
    label: "Say hello",
    detail: "Anything at all — questions, ideas, your family's story, or just to talk.",
    primary: true,
  },
  {
    email: "support@sonspeak.com",
    label: "Product help",
    detail: "Something not working, a board that won't behave, account questions.",
  },
  {
    email: "privacy@sonspeak.com",
    label: "Privacy & data requests",
    detail: "Data questions, deletion requests, and security reports.",
  },
  {
    email: "legal@sonspeak.com",
    label: "Legal",
    detail: "Contracts, terms, and anything your lawyer wants to send mine.",
  },
];

export default function Contact() {
  useSEO({
    title: "Contact us",
    description:
      "Email hello@sonspeak.com — a real person reads every message. " +
      "Dedicated inboxes for support, privacy, and legal.",
    path: "/contact",
  });

  return (
    <div className="min-h-screen bg-[color:var(--cream)]" data-testid="contact-page">
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16 pb-8">
        <p className="display text-xs font-bold uppercase tracking-[0.24em] text-[color:var(--coral-deep)] mb-3">
          Contact
        </p>
        <h1 className="display text-3xl sm:text-4xl font-extrabold leading-tight mb-4">
          Email me. I read every one.
        </h1>
        <p className="text-base sm:text-lg text-[color:var(--ink)]/80 leading-relaxed">
          No phone tree, no chatbot, no ticket numbers. Every message lands in
          my inbox — and I&apos;m the one who reads it.
        </p>
      </section>

      {/* The primary inbox */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-6">
        <a
          href="mailto:hello@sonspeak.com"
          className="brutal-card bg-[color:var(--gold)] p-6 sm:p-7 flex items-start gap-4 block hover:translate-x-[-1px] hover:translate-y-[-1px] transition-transform"
          data-testid="contact-primary-email"
        >
          <div className="shrink-0 w-12 h-12 rounded-xl border-[3px] border-[color:var(--ink)] bg-white flex items-center justify-center">
            <Mail size={22} aria-hidden />
          </div>
          <div className="min-w-0">
            <div className="display text-xl sm:text-2xl font-extrabold break-all">
              hello@sonspeak.com
            </div>
            <p className="text-sm text-[color:var(--ink)]/80 mt-1">
              The front door. If you're not sure where your message belongs,
              it belongs here.
            </p>
          </div>
        </a>
      </section>

      {/* The other inboxes */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {INBOXES.filter((i) => !i.primary).map((inbox) => (
            <a
              key={inbox.email}
              href={`mailto:${inbox.email}`}
              className="brutal-card bg-white p-5 block hover:translate-x-[-1px] hover:translate-y-[-1px] transition-transform"
              data-testid={`contact-email-${inbox.email.split("@")[0]}`}
            >
              <div className="display text-sm font-extrabold mb-1">{inbox.label}</div>
              <div className="text-sm font-semibold text-[color:var(--coral-deep)] break-all mb-2">
                {inbox.email}
              </div>
              <p className="text-xs text-[color:var(--ink-soft)] leading-relaxed">{inbox.detail}</p>
            </a>
          ))}
        </div>
      </section>

      {/* Expectations */}
      <section className="border-t-[3px] border-[color:var(--ink)] bg-[color:var(--paper)]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-12">
          <h2 className="display text-xl sm:text-2xl font-extrabold mb-6">
            What to expect
          </h2>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Clock size={18} className="shrink-0 mt-0.5" aria-hidden />
              <p className="text-sm sm:text-base leading-relaxed text-[color:var(--ink)]/85">
                <strong>It's just me, so replies are personal, not
                instant.</strong> Most messages get an answer within a few
                days; during busy stretches it can take longer. Nobody's
                message is ignored.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <MessageSquareText size={18} className="shrink-0 mt-0.5" aria-hidden />
              <p className="text-sm sm:text-base leading-relaxed text-[color:var(--ink)]/85">
                <strong>Please don't email anything urgent about a child's
                safety.</strong> SonSpeak is not an emergency tool — for
                emergencies, contact your doctor or 911.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Mail size={18} className="shrink-0 mt-0.5" aria-hidden />
              <p className="text-sm sm:text-base leading-relaxed text-[color:var(--ink)]/85">
                <strong>Your email is used only to reply to you.</strong> I
                don't add you to lists, share your address, or send marketing
                you didn't ask for.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
